export type PwaPlatform =
  | "ios-safari"
  | "ios-chrome"
  | "ios-other"
  | "android-chrome"
  | "android-other"
  | "desktop-chromium"
  | "desktop-safari"
  | "desktop-other"
  | "unknown";

export interface PwaEnvironment {
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
}

const IOS_THIRD_PARTY = /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i;
const MOBILE_PLATFORMS = new Set<PwaPlatform>([
  "ios-safari",
  "ios-chrome",
  "ios-other",
  "android-chrome",
  "android-other",
]);

function isIosDevice(env: PwaEnvironment): boolean {
  if (/iPhone|iPad|iPod/i.test(env.userAgent)) {
    return true;
  }

  return env.platform === "MacIntel" && (env.maxTouchPoints ?? 0) > 1;
}

function isAndroid(userAgent: string): boolean {
  return /Android/i.test(userAgent);
}

export function isMobilePwaPlatform(platform: PwaPlatform): boolean {
  return MOBILE_PLATFORMS.has(platform);
}

export type DesktopInstallPlatform =
  | "desktop-chromium"
  | "desktop-safari"
  | "desktop-other";

export type DesktopInstallStepId = "addressBar" | "menu" | "dock";

export type DesktopInstallStepIcon = "download" | "more" | "dock";

export interface DesktopInstallStep {
  id: DesktopInstallStepId;
  icon: DesktopInstallStepIcon;
}

export interface DesktopInstallGuide {
  platform: DesktopInstallPlatform;
  canUseNativePrompt: boolean;
  steps: DesktopInstallStep[];
}

const DESKTOP_INSTALL_GUIDES: Record<DesktopInstallPlatform, DesktopInstallGuide> =
  {
    "desktop-chromium": {
      platform: "desktop-chromium",
      canUseNativePrompt: true,
      steps: [
        { id: "addressBar", icon: "download" },
        { id: "menu", icon: "more" },
      ],
    },
    "desktop-safari": {
      platform: "desktop-safari",
      canUseNativePrompt: false,
      steps: [{ id: "dock", icon: "dock" }],
    },
    "desktop-other": {
      platform: "desktop-other",
      canUseNativePrompt: false,
      steps: [{ id: "menu", icon: "more" }],
    },
  };

export function isDesktopPwaPlatform(
  platform: PwaPlatform,
): platform is DesktopInstallPlatform {
  return platform in DESKTOP_INSTALL_GUIDES;
}

/** Steps for installing the site as an app on a computer. Phones use the stores instead. */
export function desktopInstallGuide(
  platform: PwaPlatform,
): DesktopInstallGuide | null {
  if (!isDesktopPwaPlatform(platform)) {
    return null;
  }
  return DESKTOP_INSTALL_GUIDES[platform];
}

export function detectPwaPlatform(env: PwaEnvironment): PwaPlatform {
  const userAgent = env.userAgent.trim();
  if (!userAgent) {
    return "unknown";
  }

  if (isIosDevice(env)) {
    if (/CriOS/i.test(userAgent)) {
      return "ios-chrome";
    }
    if (IOS_THIRD_PARTY.test(userAgent)) {
      return "ios-other";
    }
    return "ios-safari";
  }

  if (isAndroid(userAgent)) {
    if (/SamsungBrowser|Firefox|EdgA|OPR/i.test(userAgent)) {
      return "android-other";
    }
    if (/Chrome/i.test(userAgent)) {
      return "android-chrome";
    }
    return "android-other";
  }

  if (/Edg\/|Chrome\/|Chromium\//i.test(userAgent) && !/Firefox/i.test(userAgent)) {
    return "desktop-chromium";
  }

  if (/Safari/i.test(userAgent) && /Version\//i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    return "desktop-safari";
  }

  return "desktop-other";
}

export function didJustCompletePredictions({
  previousVoteCount,
  nextVoteCount,
  totalCategories,
}: {
  previousVoteCount: number;
  nextVoteCount: number;
  totalCategories: number;
}): boolean {
  if (totalCategories <= 0) {
    return false;
  }
  return previousVoteCount < totalCategories && nextVoteCount >= totalCategories;
}
